import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ansCasesHref } from '@/pages/ans/OverviewPage';
import { fetchAnsTimeline } from '@/services/ans';
import type { AnsInterval, AnswerOut } from '@/services/types';

const QUANTILES = ['p10', 'p25', 'p50', 'p75', 'p90'] as const;

/**
 * How long an ANS penalty takes, step by step, and how mature each cohort is.
 *
 * Every interval is measured over the rows carrying both of its dates and
 * says so; the board step describes the dated minority of reviewed cases.
 * No outcome trend: a relief rate by year would confound the board with
 * cohort maturity, so it is refused with the maturity beside it.
 */
export function AnsTimelinePage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'timeline'],
    queryFn: fetchAnsTimeline,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:timeline.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:timeline.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:timeline.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/timeline" label={t('ans:timeline.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        {data.steps.map((step, index) => (
          <StepPanel key={step.value?.step ?? index} answer={step} />
        ))}
      </div>

      <FieldGuide i18nKey="ans:timeline.guide" level={2} />

      <section className="rounded-lg border bg-card p-4" data-cohorts>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:timeline.cohorts.title')}
        </h2>
        <Answer answer={data.cohorts} help={t('ans:timeline.cohorts.help')}>
          {(rows) => (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ans:timeline.cohorts.year')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:timeline.cohorts.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:timeline.cohorts.reviewed')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:timeline.cohorts.boardDated')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:timeline.cohorts.closed')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:timeline.cohorts.paid')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.year} className="border-t" data-cohort={c.year}>
                      <td className="px-3 py-2 tabular-nums text-foreground">{c.year}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={ansCasesHref({ year: c.year })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(c.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(c.reviewed)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(c.reviewed_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(c.board_dated)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(c.closed)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(c.closed_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(c.paid)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>

      <RefusalCard title={t('ans:timeline.trend.title')} answer={data.outcome_trend} />
    </div>
  );
}

function StepPanel({ answer }: { answer: AnswerOut<AnsInterval> }) {
  const { t, i18n } = useTranslation(['ans']);
  const number = new Intl.NumberFormat(i18n.language);
  const step = answer.value?.step ?? 'first_to_publication';
  return (
    <section className="rounded-lg border bg-card p-4" data-step={step}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`ans:timeline.steps.${step}.title`)}
      </h2>
      <Answer answer={answer} help={t(`ans:timeline.steps.${step}.help`)}>
        {(interval) => (
          <div className="space-y-2">
            <dl className="grid grid-cols-5 gap-2 text-sm">
              {QUANTILES.map((q) => (
                <div key={q} className="rounded-md border bg-muted/30 p-2" data-quantile={q}>
                  <dt className="text-xs text-muted-foreground">{q}</dt>
                  <dd className="font-semibold tabular-nums text-foreground">
                    {interval.days
                      ? t('ans:timeline.days', {
                          n: number.format(interval.days.quantiles[q] ?? 0),
                        })
                      : '—'}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-muted-foreground">
              {t('ans:timeline.rows', { n: number.format(interval.rows_with_both_ends) })}
              {interval.negative > 0
                ? ` · ${t('ans:timeline.negative', { n: number.format(interval.negative) })}`
                : ''}
            </p>
          </div>
        )}
      </Answer>
    </section>
  );
}
